import { type CSSProperties, memo, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BodyCopy } from "../components/BodyCopy";
import {
  Button,
  GradientText,
  Layout,
  SuccessIcon,
  SupportIcon,
} from "lola-framework-ui-test";
import { AuraLayout } from "../components/AuraLayout";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { useWatchedRequest } from "../hooks/useWatchedRequest";
import { amountFormatter, formatHeroAmount, formatLabeledAmount } from "../utils";
import {
  receiptStatusDisplay,
  requestReceiptHeroKey,
} from "../utils/displayStatus";
import { STATUS_TONE_COLOR, type StatusTone } from "../utils/operationStatus";
import {
  StatusCircleIcon,
  StatusFailedIcon,
  StatusPendingIcon,
} from "../assets/icons";
import { APP_ROUTES } from "../constants";
import type { RequestDetail } from "../api/requests";
import { getEvents, safeCapture } from "../analytics";
import { useSupportSheet } from "../hooks/useSupportSheet";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import { usablePersonName } from "../utils/usablePersonName";
import { buildShareText, shareOrCopyMessage } from "../utils/shareMessage";
import { Navbar } from "../components/Navbar";


const formatReceiptDate = (date: Date): string => {
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatDate = (isoDate: string): string => formatReceiptDate(new Date(isoDate));

const formatReceiptDateTime = (isoDate: string): string => {
  const date = new Date(isoDate);
  return `${formatReceiptDate(date)}\n${date.toLocaleTimeString()}`;
};

/** Funds settle on the 5th business day after the operation is created. */
const availableReceiptDate = (isoDate: string): string => {
  const result = new Date(isoDate);
  let added = 0;
  while (added < 5) {
    result.setDate(result.getDate() + 1);
    const day = result.getDay();
    if (day !== 0 && day !== 6) added += 1;
  }
  return formatReceiptDate(result);
};

const money = (amount: number | undefined, currency: string): string =>
  formatLabeledAmount(amount ?? 0, currency);

const statusIcon = (tone: StatusTone, color: string) => {
  switch (tone) {
    case "completed":
      return <SuccessIcon size={10} colors={[color, color]} />;
    case "failed":
    case "expired":
      return <StatusFailedIcon size={10} color={color} />;
    case "initial":
      return <StatusPendingIcon size={10} color={color} />;
    case "progress":
    default:
      return <StatusCircleIcon size={10} color={color} />;
  }
};

/**
 * Re-share CTA only while the request is still waiting to be funded — same
 * pattern as deferred-payout's claim-link copy on the receipt.
 */
const canReshareFundRequest = (detail: RequestDetail): boolean =>
  detail.status === "pending" && Boolean(detail.fundRequestUrl);

/**
 * Receipt screen for a money request (requester perspective).
 *
 * Pending requests show a footer share CTA. Fulfilled requests show operation
 * details and no share button.
 */
export const ReceiptSummaryPage = memo(() => {
  useEffect(() => {
    safeCapture(getEvents().SCREEN_VIEW("done"));
  }, []);

  const { t, i18n } = useTranslation();
  const { theme } = useStore();
  const { session } = useSessionStore();
  const { redirectTo } = useRouteWithToken();
  const { requestId } = useParams<{ requestId: string }>();

  const colorConfig = theme.whiteView;
  const { backgroundBtn, textColorBtn, bodyCopy, dropzoneColors, footerColor } =
    colorConfig;

  const { detail, status } = useWatchedRequest(
    session.data?.linkId,
    requestId
  );
  const loading = status === "loading";
  const fetchFailed = status === "missing";

  const showShare = detail ? canReshareFundRequest(detail) : false;
  const shareUrl = showShare ? (detail?.fundRequestUrl ?? "") : "";

  const handleShare = async () => {
    if (!detail || !shareUrl) return;
    safeCapture(getEvents().SHARE_CLICK, { action: "share" });
    const message = buildShareText(t, {
      firstName: session.data?.userData?.name?.firstName,
      amount: detail.amount,
      currency: detail.corridor.destinationCurrency,
      url: shareUrl,
      language: i18n.language,
    });
    await shareOrCopyMessage(message, {
      title: t("share.shareTitle"),
      fallbackPrompt: t("share.manualPrompt"),
    });
  };

  const renderContent = () => {
    if (loading) {
      return (
        <section className="receipt-summary-loading">
          <span
            className="loader-spinner"
            style={
              {
                "--spinner-start": bodyCopy,
                "--spinner-end": bodyCopy,
              } as CSSProperties
            }
          />
        </section>
      );
    }

    if (fetchFailed || !detail) {
      return (
        <section className="receipt-summary-loading">
          <BodyCopy textColor={bodyCopy}>{t("receipt.notFound")}</BodyCopy>
        </section>
      );
    }

    const operation = detail.operation ?? null;
    const destination = detail.corridor.destinationCurrency;
    const source = detail.corridor.sourceCurrency;
    const hasOperation = operation != null;

    const heroAmount = hasOperation
      ? operation.receiveAmount
      : (detail.amount ?? 0);
    const { prefix, suffix, intPart: heroInt, decPart: heroDec } = formatHeroAmount(
      heroAmount,
      destination
    );

    const statusDisplay = receiptStatusDisplay(detail);
    const statusValue = t(statusDisplay.statusKey);
    const statusColor = STATUS_TONE_COLOR[statusDisplay.tone];
    const payerName = usablePersonName(detail.payerName);

    const summaryItems: {
      label: string;
      value: string;
      isBold?: boolean;
      statusColor?: string;
      icon?: React.ReactNode;
    }[] = hasOperation
      ? [
          {
            label: t("receipt.status"),
            value: statusValue,
            isBold: true,
            statusColor,
            icon: statusIcon(statusDisplay.tone, statusColor),
          },
          {
            label: t("receipt.transferAmount"),
            value: money(operation.sendAmount, source),
          },
          {
            label: t("receipt.transactionFee"),
            value: money(operation.transactionFee, source),
          },
          {
            label: t("receipt.exchangeRate"),
            value: `${amountFormatter(operation.exchangeRate, destination, {
              country: "en-US",
              minimumFractionDigits: 4,
              maximumFractionDigits: 4,
            })} ${destination} / ${source}`,
          },
          {
            label: t("receipt.totalPayment"),
            value: money(operation.totalCost, source),
          },
          {
            label: t("receipt.receiving"),
            value: money(operation.receiveAmount, destination),
          },
          {
            label: t("receipt.fundingFee"),
            value: money(
              operation.fundingFee ?? operation.fee ?? operation.totalFee,
              source
            ),
          },
          {
            label: t("receipt.transferTaxes"),
            value: money(operation.taxFee, source),
          },
          ...(payerName
            ? [
                {
                  label: t("receipt.sender"),
                  value: payerName,
                },
              ]
            : []),
          {
            label: t("receipt.transactionDate"),
            value: formatReceiptDateTime(operation.createdAt),
          },
          {
            label: t("receipt.dateAvailable"),
            value: availableReceiptDate(operation.createdAt),
          },
        ]
      : [
          {
            label: t("receipt.status"),
            value: statusValue,
            isBold: true,
            statusColor,
            icon: statusIcon(statusDisplay.tone, statusColor),
          },
          {
            label: t("receipt.amountRequested"),
            value: money(detail.amount, destination),
          },
          {
            label: t("receipt.dateAvailable"),
            value: formatDate(detail.createdAt),
          },
        ];

    const idLabel = hasOperation
      ? t("receipt.transactionId")
      : t("receipt.requestId");
    const idValue = hasOperation
      ? operation.operationId
      : detail.requestId;

    return (
      <section className="receipt-summary-container">
        <section className="summary-section">
          <section className="summary-title">
            <BodyCopy textColor={bodyCopy}>
              {t(requestReceiptHeroKey(detail))}
            </BodyCopy>
            <GradientText as="h1">
              {prefix}{heroInt}.<label>{heroDec}</label>
              {suffix ? (
                <span className="receipt-hero-currency">{suffix}</span>
              ) : null}
            </GradientText>
            <BodyCopy
              className="receipt-trx-caption"
              textColor={bodyCopy}
              aria-label={idLabel}
            >
              {(idValue ?? "").slice(0, 8).toUpperCase()}
            </BodyCopy>
          </section>
          <section className="summary-info">
            {summaryItems.map(({ label, value, isBold, statusColor: color, icon }, index) => (
              <SummaryInfoItem
                key={index}
                status={label}
                value={value}
                isBold={isBold}
                statusColor={color}
                icon={icon}
              />
            ))}
          </section>
        </section>
        <section className="receipt-summary-disclaimers">
          <BodyCopy className="footer-text" textColor={footerColor}>
            {t("receipt.footerNote")}
          </BodyCopy>
        </section>
      </section>
    );
  };

  const { openSupport, supportSheet } = useSupportSheet();

  return (
    <>
      <AuraLayout colorConfig={colorConfig} className="need-help-header receipt-summary-section">
        <Layout.Header>
          <Navbar
            title={t("common.needHelp")}
            align="right"
            onBackClick={() => redirectTo(APP_ROUTES.HOME_PAGE)}
            onIconClick={() => openSupport()}
            icon={<SupportIcon size={35} colors={dropzoneColors} />}
          />
        </Layout.Header>
        <Layout.Content isOverflowauto className="receipt-summary-content">
          <ScrollFadeIndicator color={bodyCopy} />
          {renderContent()}
        </Layout.Content>
        <Layout.Footer>
          <section className="footer-section transactions-footer">
            {showShare && shareUrl && (
              <section className="receipt-share-cta">
                <Button
                  size="large"
                  background={backgroundBtn}
                  color={textColorBtn}
                  onClick={() => void handleShare()}
                >
                  {t("share.share")}
                </Button>
              </section>
            )}
          </section>
        </Layout.Footer>
      </AuraLayout>
      {supportSheet}
    </>
  );
});

interface SummaryInfoItemProps {
  isBold?: boolean;
  statusColor?: string;
  status: string;
  value: string | React.ReactNode;
  icon?: React.ReactNode;
}

const SummaryInfoItem: React.FC<SummaryInfoItemProps> = ({
  isBold,
  status,
  value,
  statusColor,
  icon,
}) => (
  <section className={`summary-info-item ${isBold ? "bold" : ""} `}>
    <p>{status}</p>
    <p
      className={icon ? "summary-info-status" : undefined}
      style={{
        ...(statusColor ? { color: statusColor } : undefined),
        whiteSpace: "pre-line",
        textAlign: "right",
      }}
    >
      {icon}
      {value}
    </p>
  </section>
);
